import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button as ChakraButton,
  Text,
  VStack,
  HStack,
  useToast,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import AudioRecorder from "./AudioRecorder";
import RecordingBubble from "../components/RecordingBubble";
import { prompts, getTypeFieldIndex, colid } from "../components/env";
import {
  getLiveTranscriptionSetting,
  getLiveFactExtractionSetting,
  setLiveTranscriptionSetting,
  setLiveFactExtractionSetting,
  setAutoSaveSetting,
  getAutoSaveSetting,
} from "../utils/utils";
/**
 * RecordingPage Component
 *
 * External page that handles the recording interface with patient data and recording options.
 * This combines the functionality previously shown inline in RightSideBar.
 */
function RecordingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  // Get patient data from navigation state or localStorage (for popup windows)
  const getDataFromSources = () => {
    // First try to get from location state (regular navigation)
    if (location.state?.patientData) {
      return {
        patientData: location.state.patientData,
        userInfo: location.state.userInfo || {},
        actualPatientId: location.state.actualPatientId,
        validId: location.state.validId,
      };
    }

    // If no location state, try to get from localStorage (popup window)
    const storedData = localStorage.getItem("recordingPopupData");
    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData);
        console.log(
          "🎯 RecordingPage: Parsed data from localStorage:",
          parsedData,
        );

        // Enhanced data extraction with better fallback handling
        const patientData = parsedData.patientData || {};
        const userInfo = parsedData.userInfo || patientData || {};

        return {
          patientData: patientData,
          userInfo: userInfo,
          actualPatientId:
            parsedData.actualPatientId || patientData?.id || userInfo?.id,
          validId: parsedData.validId || patientData?.id || userInfo?.id,
          // Enhanced fields for better data access
          patientName:
            parsedData.patientName ||
            userInfo[prompts[0][0]] ||
            userInfo.patient_name,
          patientEmail:
            parsedData.patientEmail || userInfo[colid] || userInfo.email,
          patientType:
            parsedData.patientType ||
            userInfo[prompts[getTypeFieldIndex()][0]] ||
            userInfo.type,
          ehrId: parsedData.ehrId || userInfo.EHR_ID || userInfo.ehr_id,
        };
      } catch (error) {
        console.error("Error parsing recording popup data:", error);
      }
    }

    // Fallback to empty defaults with enhanced logging
    console.warn("⚠️ RecordingPage: No patient data found in any source");
    return {
      patientData: null,
      userInfo: {},
      actualPatientId: null,
      validId: null,
      patientName: null,
      patientEmail: null,
      patientType: null,
      ehrId: null,
    };
  };

  // Store patient data in state to persist across re-renders
  // Use a ref to track if we've initialized to prevent overwriting existing data
  const hasInitializedRef = useRef(false);
  const [patientDataState, setPatientDataState] = useState(() => {
    const initialData = getDataFromSources();
    // Mark as initialized if we got valid data
    if (
      initialData.patientData ||
      initialData.userInfo ||
      initialData.actualPatientId
    ) {
      hasInitializedRef.current = true;
    }
    return initialData;
  });

  // Update patient data state when location state or localStorage changes
  // but only on initial mount or if we don't already have valid data
  useEffect(() => {
    if (!hasInitializedRef.current) {
      const newData = getDataFromSources();
      // Only update if we have valid data
      if (newData.patientData || newData.userInfo || newData.actualPatientId) {
        console.log(
          "🎯 RecordingPage: Initializing patient data state from sources",
        );
        setPatientDataState(newData);
        hasInitializedRef.current = true;
      }
    }
  }, [location.state]);

  // Use state values instead of calling getDataFromSources() on every render
  const data = patientDataState;
  const patientData = data.patientData;
  const userInfo = data.userInfo;
  const actualPatientId = data.actualPatientId;
  const validId = data.validId;

  // Recording state - these will be passed to the global AudioRecorder
  const [showPersistentComponent, setShowPersistentComponent] = useState(false);
  const [stateData, setStateData] = useState({});
  const [session, setSession] = useState(false);
  const [condFunc, setCondFunc] = useState(undefined);
  const [override, setOverride] = useState(false);
  const [refId, setRefId] = useState(undefined);
  const [recordingComponentState, setRecordingComponentState] = useState(() => {
    try {
      return localStorage.getItem("audioRecorderRecordingState") || "closed";
    } catch {
      return "closed";
    }
  });

  // Checkbox states for recording options
  const [liveTranscriptionEnabled, setLiveTranscriptionEnabled] =
    useState(false);
  const [liveFactExtractionEnabled, setLiveFactExtractionEnabled] =
    useState(false);
  const [autoSaveSetting, setAutoSaveSetting] = useState(false);
  const [userManuallyChangedAutoSave, setUserManuallyChangedAutoSave] =
    useState(false);

  // Refs to track current state for async functions
  const liveTranscriptionEnabledRef = useRef(false);
  const liveFactExtractionEnabledRef = useRef(false);
  const autoSaveSettingRef = useRef(false);

  // Keep refs in sync with state
  useEffect(() => {
    liveTranscriptionEnabledRef.current = liveTranscriptionEnabled;
  }, [liveTranscriptionEnabled]);

  useEffect(() => {
    liveFactExtractionEnabledRef.current = liveFactExtractionEnabled;
  }, [liveFactExtractionEnabled]);

  useEffect(() => {
    autoSaveSettingRef.current = autoSaveSetting;
  }, [autoSaveSetting]);

  // Initialize checkbox states from localStorage on component load
  useEffect(() => {
    const currentPatientId = patientData?.id || actualPatientId || validId;

    if (currentPatientId) {
      // Get saved settings for this patient
      const savedLiveTranscription =
        getLiveTranscriptionSetting(currentPatientId);
      const savedLiveFactExtraction =
        getLiveFactExtractionSetting(currentPatientId);
      const savedAutoSaveSetting = getAutoSaveSetting(currentPatientId);

      // Set checkboxes based on saved settings (default to false if not set)
      setLiveTranscriptionEnabled(savedLiveTranscription || false);
      setLiveFactExtractionEnabled(savedLiveFactExtraction || false);
      setAutoSaveSetting(
        savedAutoSaveSetting !== null ? savedAutoSaveSetting : false,
      );
    } else {
      // Default to false if no patient ID
      setLiveTranscriptionEnabled(false);
      setLiveFactExtractionEnabled(false);
      setAutoSaveSetting(false);
    }
  }, [patientData, actualPatientId, validId]);

  // Sync the loaded patient info to the local stateData and refId so AudioRecorder works
  useEffect(() => {
    const activePatient = patientData || userInfo || data;
    if (activePatient && Object.keys(activePatient).length > 0) {
      console.log("🎯 [RecordingPage] Syncing patient stateData:", activePatient);
      setStateData(activePatient);
    }
    const finalPatientId =
      patientData?.id ||
      actualPatientId ||
      validId ||
      data.actualPatientId ||
      data.validId;
    if (finalPatientId) {
      console.log("🎯 [RecordingPage] Syncing patient refId:", finalPatientId);
      setRefId(finalPatientId);
    }
  }, [patientData, userInfo, data, actualPatientId, validId]);


  // Keep in-page state synced with the recorder lifecycle so we can prevent
  // accidental navigation while an external recording window is active.
  useEffect(() => {
    const handleRecordingComponentStateChange = (event) => {
      if (event?.detail?.state) {
        setRecordingComponentState(event.detail.state);
      }
    };

    window.addEventListener(
      "recordingComponentStateChange",
      handleRecordingComponentStateChange,
    );
    return () => {
      window.removeEventListener(
        "recordingComponentStateChange",
        handleRecordingComponentStateChange,
      );
    };
  }, []);

  // Browser/tab close protection during active external recording window.
  useEffect(() => {
    const isRecordingFlowActive = !["closed", "prompt"].includes(
      recordingComponentState,
    );
    if (!isRecordingFlowActive) return;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue =
        "A recording window is still active. Leaving may interrupt save/transcription.";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [recordingComponentState]);

  // Handler for live transcription checkbox changes
  const handleLiveTranscriptionChange = (e) => {
    const newValue = e.target.checked;
    setLiveTranscriptionEnabled(newValue);

    // Sync live fact extraction with live transcription
    setLiveFactExtractionEnabled(newValue);

    // Auto-save should sync with live transcription if user hasn't manually changed it
    if (!userManuallyChangedAutoSave) {
      setAutoSaveSetting(newValue);
    }

    // Save to localStorage for this patient
    const currentPatientId = patientData?.id || actualPatientId || validId;
    if (currentPatientId) {
      setLiveTranscriptionSetting(currentPatientId, newValue);
      setLiveFactExtractionSetting(currentPatientId, newValue);

      if (!userManuallyChangedAutoSave) {
        setAutoSaveSetting(currentPatientId, newValue);
      }
    }

    toast({
      title: newValue
        ? "Live Transcription Enabled"
        : "Live Transcription Disabled",
      description: newValue
        ? "Your speech will be transcribed in real-time during recording."
        : "Live transcription has been disabled.",
      status: "info",
      duration: 3000,
      isClosable: true,
    });
  };

  // Handler for live fact extraction checkbox changes
  const handleLiveFactExtractionChange = (e) => {
    const newValue = e.target.checked;
    setLiveFactExtractionEnabled(newValue);

    // Live transcription should sync with live fact extraction
    if (newValue) {
      setLiveTranscriptionEnabled(true);

      // Auto-save should sync if user hasn't manually changed it
      if (!userManuallyChangedAutoSave) {
        setAutoSaveSetting(true);
      }

      // Save to localStorage
      const currentPatientId = patientData?.id || actualPatientId || validId;
      if (currentPatientId) {
        setLiveTranscriptionSetting(currentPatientId, true);
        setLiveFactExtractionSetting(currentPatientId, newValue);

        if (!userManuallyChangedAutoSave) {
          setAutoSaveSetting(currentPatientId, true);
        }
      }
    } else {
      // When disabling fact extraction, don't automatically disable transcription
      // Let the user control that separately
      const currentPatientId = patientData?.id || actualPatientId || validId;
      if (currentPatientId) {
        setLiveFactExtractionSetting(currentPatientId, newValue);
      }
    }

    toast({
      title: newValue
        ? "Live Fact Extraction Enabled"
        : "Live Fact Extraction Disabled",
      description: newValue
        ? "Medical facts will be extracted in real-time during recording."
        : "Live fact extraction has been disabled.",
      status: "info",
      duration: 3000,
      isClosable: true,
    });
  };

  // Handler for auto-save checkbox changes
  const handleAutoSaveChange = (e) => {
    const newValue = e.target.checked;
    setAutoSaveSetting(newValue);
    setUserManuallyChangedAutoSave(true); // Mark that user has manually changed this setting

    // Save to localStorage for this patient
    const currentPatientId = patientData?.id || actualPatientId || validId;
    if (currentPatientId) {
      setAutoSaveSetting(currentPatientId, newValue);
    }

    toast({
      title: newValue ? "Auto-save Enabled" : "Auto-save Disabled",
      description: newValue
        ? "Transcription results will be automatically saved after recording."
        : "Transcription results will need to be saved manually.",
      status: "info",
      duration: 3000,
      isClosable: true,
    });
  };

  // Start recording handler
  const handleStartRecording = () => {
    console.log(
      "📝 RecordingPage: Start Recording button clicked - delegating to RecordingBubble",
    );
    console.log("🎯 RecordingPage: Current data state:", {
      patientData,
      userInfo,
      actualPatientId,
      validId,
      data,
    });

    // Prepare comprehensive patient data for the global audio recorder
    // Merge data from multiple sources with proper fallbacks
    const stateForRecorder = {
      // Start with patientData if available
      ...(patientData || {}),
      // Merge with userInfo (userInfo takes precedence for overlapping keys)
      ...(userInfo || {}),
      // Add specific enhanced fields from our data extraction
      ...(data.patientName && { [prompts[0][0]]: data.patientName }),
      ...(data.patientEmail && { [colid]: data.patientEmail }),
      ...(data.patientType && {
        [prompts[getTypeFieldIndex()][0]]: data.patientType,
      }),
      ...(data.ehrId && { EHR_ID: data.ehrId }),
    };

    console.log(
      "🎯 RecordingPage: Prepared state for recorder:",
      stateForRecorder,
    );

    // Set the patient ID with enhanced fallbacks
    const finalPatientId =
      patientData?.id ||
      actualPatientId ||
      validId ||
      data.actualPatientId ||
      data.validId;
    console.log("🎯 RecordingPage: Delegating to RecordingBubble for patient:", finalPatientId);

    // Keep bubble patient context in sync before opening.
    window.dispatchEvent(
      new CustomEvent("patientContextUpdate", {
        detail: {
          patientId: finalPatientId,
          patientData: [stateForRecorder],
        },
      }),
    );

    // Single entry path: use RecordingBubble for all "start recording" actions.
    const openEvent = new CustomEvent("recordingBubbleOpen", {
      detail: { source: "RecordingPage", handled: false },
    });
    window.dispatchEvent(openEvent);

    if (!openEvent.detail?.handled) {
      toast({
        title: "Recording Bubble not available",
        description:
          "Please open recording from the red recording bubble on the patient page.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  // Go back handler
  const handleGoBack = () => {
    const isRecordingFlowActive = !["closed", "prompt"].includes(
      recordingComponentState,
    );
    if (isRecordingFlowActive) {
      const shouldLeave = window.confirm(
        "A recording window is still active. Leaving now can interrupt audio/transcription saving. Leave anyway?",
      );
      if (!shouldLeave) return;
    }
    navigate(-1);
  };

  return (
    <Box p={6} maxW="1200px" mx="auto">
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between" align="center">
          <HStack spacing={4}>
            <ChakraButton
              variant="outline"
              size="sm"
              onClick={handleGoBack}
              leftIcon={<FontAwesomeIcon icon={faArrowLeft} />}
            >
              Back
            </ChakraButton>
            <Text fontSize="2xl" fontWeight="bold" color="blue.600">
              Audio Recording
            </Text>
          </HStack>
        </HStack>

        {/* Patient Info */}
        <Box p={4} borderWidth="1px" borderRadius="md" bg="gray.50">
          <Text fontWeight="bold" mb={2} color="gray.700">
            Patient Information
          </Text>
          <VStack align="start" spacing={1}>
            <Text fontSize="md">
              <strong>Name:</strong>{" "}
              {userInfo[prompts[0][0]] ||
                patientData?.[prompts[0][0]] ||
                data.patientName ||
                "Unknown"}
            </Text>
            {data.patientEmail && (
              <Text fontSize="md">
                <strong>Email:</strong> {data.patientEmail}
              </Text>
            )}
            {data.patientType && (
              <Text fontSize="md">
                <strong>Type:</strong> {data.patientType}
              </Text>
            )}
            {data.ehrId && (
              <Text fontSize="md">
                <strong>EHR ID:</strong> {data.ehrId}
              </Text>
            )}
            {data.actualPatientId && (
              <Text fontSize="sm" color="gray.600">
                <strong>Patient ID:</strong> {data.actualPatientId}
              </Text>
            )}
          </VStack>
        </Box>

        {/* Recording Options */}
        <Box p={6} borderWidth="1px" borderRadius="md" bg="gray.50">
          <Text fontWeight="bold" mb={4} color="blue.600" fontSize="lg">
            Recording Options
          </Text>

          <VStack spacing={4} align="stretch">
            {/* Live Transcription Checkbox */}
            <Box>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={liveTranscriptionEnabled}
                  onChange={handleLiveTranscriptionChange}
                  style={{ marginRight: "12px", transform: "scale(1.2)" }}
                />
                <VStack align="start" spacing={0}>
                  <Text fontSize="md" fontWeight="medium">
                    Live Transcription
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    Convert speech to text in real-time during recording
                  </Text>
                </VStack>
              </label>
            </Box>

            {/* Live Fact Extraction Checkbox */}
            <Box>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={liveFactExtractionEnabled}
                  onChange={handleLiveFactExtractionChange}
                  style={{ marginRight: "12px", transform: "scale(1.2)" }}
                />
                <VStack align="start" spacing={0}>
                  <Text fontSize="md" fontWeight="medium">
                    Live Fact Extraction
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    Automatically extract medical facts and information during
                    recording
                  </Text>
                </VStack>
              </label>
            </Box>

            {/* Auto-save Checkbox */}
            <Box>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={autoSaveSetting}
                  onChange={handleAutoSaveChange}
                  style={{ marginRight: "12px", transform: "scale(1.2)" }}
                />
                <VStack align="start" spacing={0}>
                  <Text fontSize="md" fontWeight="medium">
                    Auto-save transcription results
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    Automatically save transcription results after recording
                    completes
                  </Text>
                </VStack>
              </label>
            </Box>
          </VStack>
        </Box>

        {/* Start Recording Bubble */}
        <Box textAlign="center">
          <VStack spacing={2}>
            <Text fontSize="sm" color="gray.600">
              Start recording using the recording bubble
            </Text>
            <RecordingBubble
              currentPatientId={
                patientData?.id ||
                actualPatientId ||
                validId ||
                data.actualPatientId ||
                data.validId
              }
              currentPatientData={patientData || userInfo || data || {}}
            />
          </VStack>
        </Box>

        {/* Audio Recorder Component */}
        <AudioRecorder
          state={stateData}
          refId={refId}
          session={session}
          setSession={setSession}
          override={override}
          setShowPersistentComponent={setShowPersistentComponent}
          condFunc={condFunc}
          autoSaveSetting={autoSaveSetting}
        />
      </VStack>
    </Box>
  );
}

export default RecordingPage;
